<template>
  <div class="w-full flex justify-center">
    <div class="main-container">
      <div class="heading pb-6 flex items-center">
        <h2 class="font-normal text-zt-primary-200">
          {{ $t('message.publishDocuments.configGenericPublish.title') }}
        </h2>
      </div>
      <div class="main rounded relative">
        <div v-if="isLoading" class="absolute w-full top-0 left-0 z-10 h-full">
          <vue-element-loading
            :active="isLoading"
            spinner="spinner"
            duration="1.5"
            color="#4f4f4f"
            size="50"
          />
        </div>
        <div class="folder-form-container relative w-full h-full">
          <div ref="container">
            <div class="px-32 py-16">
              <div class="mb-12 flex items-center justify-start">
                <h2 class="font-medium text-3xl">
                  {{
                    $t('message.publishDocuments.configGenericPublish.subtitle')
                  }}
                </h2>
              </div>

              <div class="pt-14">
                <div class="w-full flex mb-8">
                  <router-link
                    class="flex-1 bg-blue-500 text-base font-normal text-white py-3 flex items-center justify-center rounded-l transition duration-150 ease-in-out"
                    to="/documents/publish/generic"
                  >
                    {{ $t('message.publishDocuments.configGenericPublish.tab.byCategory') }}
                  </router-link>
                  <a
                    class="flex-1 bg-blue-500 text-base font-normal text-white py-3 flex items-center justify-center rounded-r transition duration-150 ease-in-out"
                    href="/v3/documents/publish/generic/post"
                  >
                    {{ $t('message.publishDocuments.configGenericPublish.tab.byFile') }}
                  </a>
                </div>
              </div>

              <div
                v-if="shownAlert && statePublish.documentSignature"
                class="mb-12"
              >
                <zt-alert
                  color="info"
                  :filled="true"
                  :close-icon="false"
                  @afterClosed="shownAlert = false"
                  class="p-4 border border-zt-secondary-100"
                >
                  <template slot="custom-content">
                    <div class="flex justify-start items-center">
                      <zt-icon
                        name="info-circle-solid"
                        class="text-2xl mr-4"
                        :style="{ 'font-size': '2rem' }"
                      ></zt-icon>
                      <p class="font-normal text-zt-base">
                        {{
                          $t(
                            'message.publishDocuments.configGenericPublish.withOutConfig'
                          )
                        }}
                      </p>
                      <div class="inline-block ml-4">
                        <zt-button
                          color="secondary-100"
                          outlined
                          @click="goToConfig"
                          class="button-alert"
                        >
                          {{
                            $t(
                              'message.publishDocuments.configGenericPublish.configSign'
                            )
                          }}
                        </zt-button>
                      </div>
                    </div>
                  </template>
                </zt-alert>
              </div>
              <input-document-type :documents="documents" ref="inputDocType" />
              <div class="mb-16">
                <h2 class="custom-title custom-color-gray font-medium mb-4">
                  {{
                    $t(
                      'message.publishDocuments.configGenericPublish.inputSector.question'
                    )
                  }}
                </h2>
                <p class="custom-legend text-zt-dark-300 mb-4">
                  {{
                    $t(
                      'message.publishDocuments.configGenericPublish.inputSector.description'
                    )
                  }}
                </p>
                <input-sector
                  class="mb-12"
                  :sectors="sectors"
                  ref="inputSector"
                />
                <input-location
                  class="mb-12"
                  :locations="locations"
                  ref="inputLocation"
                />
                <input-cargo
                  class="mb-12"
                  :position="position"
                  ref="inputCargo"
                />
              </div>
              <check-notification-mail />
              <publish-load-document ref="inputFile" />
            </div>
            <div class="footer flex justify-between items-center px-10 h-24">
              <div class="flex justify-end w-full">
                <zt-button
                  text
                  class="button-cancel mr-4"
                  @click="redirectTo"
                  data-vs-button="publish-generic-cancel"
                >
                  {{
                    $t(
                      'message.publishDocuments.configGenericPublish.action.cancel'
                    )
                  }}
                </zt-button>
                <zt-button
                  :disabled="!isButtonAvailable"
                  :loader="loadingButton"
                  :class="{
                    'text-zt-dark-b6': !isButtonAvailable,
                    'text-zt-dark-200': !isButtonAvailable
                  }"
                  @click="publish"
                  data-vs-button="publish-generic-send"
                >
                  {{
                    $t(
                      `message.publishDocuments.configGenericPublish.action.publish`
                    )
                  }}
                </zt-button>
              </div>
            </div>
          </div>
          <modal-notification-user :isActiveModalInfo="isActiveModalInfo" />
          <modal-notification-cancel :isActiveModalInfo="isActiveModalCancel" />
          <modal-complete-generic-publish
            :isActiveModalInfo="isActiveConfirmModal"
          />
          <modal-notification-error :isActiveModalError="isActiveErrorModal" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import Folders from '@/apiClient/Folders'
import User from '@/apiClient/User'
import InputDocumentType from '../components/generic/inputDocumentType.vue'
import InputSector from '../components/generic/inputSector.vue'
import InputLocation from '../components/generic/inputLocation.vue'
import InputCargo from '../components/generic/inputCargo.vue'
import CheckNotificationMail from '../components/generic/checkNotificationMail.vue'
import PublishLoadDocument from '../components/generic/publishLoadDocument'
import ModalNotificationUser from '../components/modals/ModalNotificationUser.vue'
import ModalNotificationCancel from '../components/modals/ModalNotificationCancel.vue'
import ModalCompleteGenericPublish from '../components/modals/ModalCompleteGenericPublish.vue'
import ModalNotificationError from '@/modules/Documents/publish/components/modals/ModalNotificationError'
import usePublishGenericState from '@/modules/Documents/publish/hooks/usePublishGenericState'
import usePublishModal from '@/modules/Documents/publish/hooks/usePublishModal'
import MultiSelect from 'vue-multiselect'

const { state: statePublish, resetState } = usePublishGenericState()
const {
  store: NotificationModalStore,
  setActiveModal: setActiveNotificationModal,
  resetModalState: notificationClearModalState
} = usePublishModal()

const {
  store: NotificationModalCancelStore,
  setActiveModal: setActiveNotificationCancelModal,
  resetModalState: cancelClearModalState
} = usePublishModal()

const {
  store: ConfirmModalStore,
  setActiveModal: setActiveConfirmModal,
  resetModalState: confirmClearModalState
} = usePublishModal()

const {
  store: notificationModalCertStore,
  setActiveModal: setActiveCertModal,
  resetModalState: notificationCertModalState
} = usePublishModal()

const {
  store: errorModalStore,
  setActiveModal: setActiveErrorModal,
  resetModalState: notificationErrorModalState
} = usePublishModal()

export default {
  name: 'PublishDocument',
  components: {
    MultiSelect,
    InputDocumentType,
    InputSector,
    InputLocation,
    InputCargo,
    CheckNotificationMail,
    PublishLoadDocument,
    ModalNotificationUser,
    ModalNotificationCancel,
    ModalCompleteGenericPublish,
    ModalNotificationError,
    VueElementLoading
  },
  provide() {
    return {
      statePublish,
      NotificationModalStore,
      publishDocument: this.publishSingleDocument,
      setActiveNotificationModal: setActiveNotificationModal,
      setActiveConfirmModal: setActiveConfirmModal,
      setActiveCertModal: setActiveCertModal,
      ConfirmModalStore: ConfirmModalStore,
      notificationModalCertStore: notificationModalCertStore,
      clearPublish: this.clearFormPublish,
      errorModalStore: errorModalStore,
      setActiveErrorModal: setActiveErrorModal,
      NotificationModalCancelStore: NotificationModalCancelStore,
      setActiveNotificationCancelModal: setActiveNotificationCancelModal,
      cancelClearModalState: cancelClearModalState
    }
  },
  data() {
    return {
      switchKey: 0,
      documents: [],
      sectors: [],
      locations: [],
      position: [],
      foldersInstance: new Folders(),
      userInstance: new User(),
      notification: true,
      sendPublicationActive: false,
      statePublish: statePublish,
      loadingButton: false,
      shownAlert: false,
      pending: false,
      isLoading: true
    }
  },
  computed: {
    isButtonAvailable() {
      return (
        this.statePublish.documentType &&
        this.statePublish.file &&
        this.statePublish.locations.length &&
        this.statePublish.sectors.length &&
        this.statePublish.position.length &&
        !this.pending
      )
    },
    isActiveModalCancel() {
      return NotificationModalCancelStore.active
    },
    isActiveModalInfo() {
      return NotificationModalStore.active
    },
    isActiveConfirmModal() {
      return ConfirmModalStore.active
    },
    isActiveCertModal() {
      return notificationModalCertStore.active
    },
    isActiveErrorModal() {
      return errorModalStore.active
    }
  },
  methods: {
    async getFoldersGroup() {
      try {
        const response = await this.foldersInstance.getFoldersGroup({
          hideEforms: true,
          type: 'pdf',
          withoutCode: true
        })
        const {
          data: { data }
        } = response
        this.documents = data.map((item) => {
          item.childs = item.childs.map((child) => {
            return {
              ...child,
              firmable: item.firmable
            }
          })
          return item
        })
      } catch (error) {
        this.documents = []
      }
    },
    async getSectorsGroup() {
      try {
        const response = await this.userInstance.listSector()
        const {
          data: {
            results: { data }
          }
        } = response
        this.sectors = ['TODOS', ...data.filter((item) => item.trim() !== '')]
      } catch (error) {
        this.sectors = ['TODOS']
      }
    },
    async getLocationsGroup() {
      try {
        const response = await this.userInstance.listLocacion()
        const {
          data: {
            results: { data }
          }
        } = response
        this.locations = ['TODOS', ...data.filter((item) => item.trim() !== '')]
      } catch (error) {
        this.locations = ['TODOS']
      }
    },
    async getCargosGroup() {
      try {
        const response = await this.userInstance.listPosition()
        const {
          data: {
            results: {
              data: [positions]
            }
          }
        } = response

        this.position = [
          'TODOS',
          ...positions
            .filter((item) => item && item.trim() !== '')
            .map((item) => item.toUpperCase())
        ]
      } catch (error) {
        this.position = ['TODOS']
      }
    },
    async publish() {
      try {
        this.pending = true
        
        if (!this.statePublish.documentSignature) {
          this.pending = false
          await setActiveNotificationModal(true)
          return
        }
        await setActiveCertModal(true)
      } catch (error) {
        console.error('Error in publish:', error)
        this.pending = false
        await setActiveCertModal(true)
      }
    },

    async publishSingleDocument() {
      this.sendPublicationActive = true
      const params = this.buildParams()
      try {
        await this.foldersInstance.publishGenericDocument(params)
        if (this.isActiveModalInfo) {
          setActiveNotificationModal(false)
        }
        setActiveConfirmModal(true)
        
        this.clearFormPublish()
        this.sendPublicationActive = false
        this.pending = false
      } catch (e) {
        console.error('Error in publishSingleDocument:', e)
        this.sendPublicationActive = false
        this.pending = false
        if (e.response) {
          const { code } = e.response.data.codes[0]
          errorModalStore.error = {
            shown: true,
            message: this.$t(
              `message.publishDocuments.configGenericPublish.errors.${code}`
            )
          }
          setActiveErrorModal(true)
        } else {
          setActiveConfirmModal(true)
        }
      }
    },

    buildParams() {
      return {
        file: this.statePublish.file,
        sendMail: this.statePublish.documentNotification,
        documentTypeId: this.statePublish.documentType.id,
        location: this.statePublish.locations,
        sector: this.statePublish.sectors,
        position: this.statePublish.position
      }
    },

    clearFormPublish() {
      resetState()
      
      this.$nextTick(() => {
        if (this.$refs.inputFile) {
          this.$refs.inputFile.onClear()
        }
        if (this.$refs.inputDocType) {
          this.$refs.inputDocType.onReset()
        }
        if (this.$refs.inputSector) {
          this.$refs.inputSector.onReset()
        }
        if (this.$refs.inputLocation) {
          this.$refs.inputLocation.onReset()
        }
        if (this.$refs.inputCargo) {
          this.$refs.inputCargo.onReset()
        }
      })
      
      notificationClearModalState()
      confirmClearModalState()
      notificationCertModalState()
      notificationErrorModalState()
      
      this.pending = false
      this.sendPublicationActive = false
    },

    scrollTo() {
      this.$nextTick(() => {
        this.$refs.container.scrollIntoView({ behavior: 'smooth' })
      })
    },

    redirectTo() {
      if (
        this.statePublish.documentType ||
        this.statePublish.file ||
        this.statePublish.locations.length ||
        this.statePublish.sectors.length ||
        this.statePublish.position.length
      ) {
        NotificationModalCancelStore.active = true
      } else {
        this.$router.push({ name: 'MainDocumentPage' })
        this.clearFormPublish()
      }
    }
  },
  async mounted() {
    await this.clearFormPublish()
    await this.getFoldersGroup()
    await this.getSectorsGroup()
    await this.getLocationsGroup()
    await this.getCargosGroup()
    this.isLoading = false
  }
}
</script>

<style scoped lang="scss">
.custom-title {
  font-size: 1.5rem;
}
.custom-color-gray {
  color: #575757;
}
.main-container {
  width: 650px;
  height: calc(100vh - 120px);
  .heading {
    height: 50px;
    h2 {
      color: #707070;
      font-size: 24px;
    }
  }
  .main {
    height: calc(100% - 50px);
    background: white;
    box-shadow: 0 3px 4px rgba(0, 0, 0, 0.14), 0 3px 3px rgba(0, 0, 0, 0.12),
      0 1px 8px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.3s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
.folder-form-container {
  overflow-y: auto;
  .close-folder {
    font-size: 24px;
    line-height: 1;
    position: absolute;
    right: 2.5rem;
    top: 4.85rem;
  }
}
.folder-form-container::-webkit-scrollbar-track {
  background-color: #c4c4c4;
}
.folder-form-container::-webkit-scrollbar {
  width: 0.625rem;
}
.folder-form-container::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: #888888;
}

.expand-to-2 {
  grid-column: span 2;
}

::v-deep .input-group {
  display: none;
}
::v-deep .zt-input-main .zt-label {
  margin-bottom: 0;
}
::v-deep .multiselect {
  min-height: 0;
}
::v-deep {
  button.button-alert {
    background: #e1f5fe !important;
    border: #03a9f4 solid 1px !important;
  }
  .border.border-zt-secondary-100 {
    border: #03a9f4 solid 1px !important;
  }
}
.footer {
  background: #e5e5e5;
  border-top: 1px solid #c4c4c4;
  font-size: 1.125rem;
  .text-zt-dark-b6 {
    color: #b6b6b6 !important;
  }
  ::v-deep {
    .button-cancel {
      color: #707070 !important;
      background: transparent !important;
    }
    button.button-delete {
      padding: 0 !important;
    }
    .button-cancel.text-zt-danger {
      color: #f16b6c !important;
      &:hover {
        span {
          border-bottom: solid 1px #f16b6c;
          line-height: 1;
        }
      }
    }
  }
}

a:not(.router-link-active) {
  background-color: #ffffff;
  border: solid 1px #ccc;
  color: #707070!important;
  font-size: 16px;
}

a:not(.router-link-active):hover {
  background-color: rgb(235, 235, 235);
}

.router-link-active {
  border-width: 1px;
  border-color: rgb(79, 65, 163);
  background-color: rgb(79, 65, 163);
  color: rgb(255, 255, 255);
  font-size: 16px;
}
</style>
